import difference from 'lodash/difference';
import intersection from 'lodash/intersection';

import type { AnyDocument, DocumentContent, FirstClassDocument } from '../types';
import { Category, isAudiobookDocumentContent } from '../types';
import type {
  AudiobookDecryptedFileMap,
  AudiobookEncryptedFileMap,
  AudiobookManifest,
  AudiobookManifestEntry,
  AudiobookTrackMetadata,
} from '../types/audiobooks';
import { sortManifestByIndex } from '../utils/audiobooks';
import nowTimestamp from '../utils/dates/nowTimestamp';
// eslint-disable-next-line import/no-cycle
import { forceReRegister } from '../utils/deviceKeyRegistration.platform';
import makeLogger from '../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import { audiobookFileStore } from './audiobookFileStore.platform';
// eslint-disable-next-line import/no-cycle
import { decryptAudiobookFileMap } from './decryptAudiobookFileMap';
import { FastCache } from './FastCache.platform';
import { createManifestLoadingTracker } from './manifestLoadingTracker';
// eslint-disable-next-line import/no-cycle
import { portalGate as foregroundPortalGate } from './portalGates/toForeground';
// eslint-disable-next-line import/no-cycle
import stores from './stores';
import { DeviceNotRegisteredError } from './stores/ItemFetcher';

const logger = makeLogger(__filename);

const MAX_TRACKS_PER_BATCH = 5;
const MIN_TIME_BETWEEN_LOADS_FROM_SERVER = 3000; // millis

type DocTrackBatch = {
  docId: string;
  parsedDocId: string;
  manifest: AudiobookManifest;
  encryptedFileMap?: AudiobookEncryptedFileMap;
  trackIds: string[];
  profileId?: number;
};

let hasStartedBackgroundLoop = false;

const audiobookProgressTracker = createManifestLoadingTracker<AudiobookManifestEntry, AudiobookTrackMetadata>({
  eventName: 'audiobookTracksDownloaded',
  itemName: 'track',
});

type QueuedManifestData = {
  manifest: AudiobookManifest;
  encryptedFileMap?: AudiobookEncryptedFileMap;
  parsedDocId: string;
  profileId?: number;
};

const queuedManifestsCache = FastCache.createInstance({
  name: 'queuedAudiobookManifestsToLoad',
});

const listenForTrackLoadingProgress = (docId: string, entries: AudiobookManifestEntry[]): void => {
  audiobookProgressTracker.listenForEntries(docId, entries);
};

export async function queueLoadForAudiobookManifest(
  docId: string,
  parsedDocId: string,
  manifest: AudiobookManifest,
  encryptedFileMap?: AudiobookEncryptedFileMap,
  profileId?: number,
) {
  const queueData: QueuedManifestData = { manifest, encryptedFileMap, parsedDocId, profileId };
  await queuedManifestsCache.setItem(docId, queueData);
  listenForTrackLoadingProgress(docId, Object.values(manifest));
  const encryptedTrackCount = encryptedFileMap ? Object.keys(encryptedFileMap).length : 0;
  logger.info('Queued audiobook manifest for download', {
    docId,
    trackCount: Object.keys(manifest).length,
    hasEncryptedTracks: encryptedTrackCount > 0,
    encryptedTrackCount,
    manifestKeys: Object.keys(manifest),
    manifestInternalIds: Object.values(manifest).map((entry) => entry.internal_id),
  });
}

export async function loadAudiobookTracks(
  docId: string,
  parsedDocId: string,
  manifest: AudiobookManifest,
  encryptedFileMap?: AudiobookEncryptedFileMap,
  prioritizedTrackIds?: string[],
  profileId?: number,
): Promise<AudiobookTrackMetadata[]> {
  const allTrackIds = extractTrackIdsFromManifest(manifest);
  const downloadedTracks = await getLocallyCachedTracks(manifest);
  const downloadedTrackIds = downloadedTracks.map((track) => track.id);

  const remainingTrackIds = allTrackIds.filter((id) => !downloadedTrackIds.includes(id));
  const tracksToDownload = prioritizedTrackIds && prioritizedTrackIds.length > 0
    ? [...intersection(prioritizedTrackIds, remainingTrackIds), ...difference(remainingTrackIds, prioritizedTrackIds)]
    : remainingTrackIds;

  if (tracksToDownload.length > 0) {
    logger.info('Downloading audiobook immediately', {
      docId,
      trackCount: tracksToDownload.length,
      prioritizedCount: prioritizedTrackIds?.length ?? 0,
    });

    const fileMapToUse = await refreshEncryptedFileMapIfNeeded(
      docId, parsedDocId, manifest, encryptedFileMap, tracksToDownload,
    );

    const decryptedFileMap: AudiobookDecryptedFileMap | undefined = profileId
      ? await decryptAudiobookFileMap(fileMapToUse, {
        profileId,
        logContext: { docId, parsedDocId },
      })
      : fileMapToUse as AudiobookDecryptedFileMap | undefined;

    await audiobookFileStore.downloadTracks(docId, manifest, decryptedFileMap ?? null, {
      trackIdsInPriorityOrder: tracksToDownload,
    });
  } else if (prioritizedTrackIds && prioritizedTrackIds.length > 0) {
    // Emit TRACK_DOWNLOADED events for already-cached prioritized tracks
    // so the state machine knows they're available
    const cachedPriorityTracks = downloadedTracks.filter((track) =>
      prioritizedTrackIds.includes(track.id));

    for (const track of cachedPriorityTracks) {
      foregroundPortalGate.emit('audiobookSingleTrackDownloaded', {
        docId,
        trackId: track.id,
        trackMetadata: track,
      });
    }
  }

  return getLocallyCachedTracks(manifest);
}

export async function removeQueuedAudiobookManifestsForDocIds(docIds: string[]): Promise<void> {
  if (docIds.length === 0) {
    return;
  }
  await queuedManifestsCache.removeItems(docIds);
}

export async function removeTracksByIds(trackIds: string[]): Promise<void> {
  if (trackIds.length === 0) {
    return;
  }
  await audiobookFileStore.removeTracksByIds(trackIds);
}

export async function clearAudiobookCache(): Promise<void> {
  await Promise.all([
    queuedManifestsCache.clear(),
    audiobookFileStore.clearAudiobookCache(),
  ]);
}

/**
 * Returns all currently downloaded tracks for an audiobook, sorted by track index.
 */
export async function getLocallyCachedTracks(manifest: AudiobookManifest): Promise<AudiobookTrackMetadata[]> {
  const trackIds = extractTrackIdsFromManifest(manifest);
  if (trackIds.length === 0) {
    return [];
  }

  const loadedTracks = audiobookFileStore.loadFromCache(trackIds);

  const indexById = new Map(Object.values(manifest).map((entry) => [entry.id, entry.index]));
  const sortedTracks = Object.values(loadedTracks).sort((a, b) => {
    return (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0);
  });

  return sortedTracks;
}

/**
 * Waits for all tracks of an audiobook to be downloaded.
 * Returns immediately if already downloaded, otherwise listens for download events.
 * Throws an error if the download doesn't complete within the timeout period.
 */
export async function waitForAudiobookDownload(
  docId: string,
  manifest: AudiobookManifest,
  timeoutMs = 60000,
): Promise<AudiobookTrackMetadata[]> {
  const startTime = nowTimestamp();
  const totalExpectedTracks = Object.keys(manifest).length;

  const existingTracks = await getLocallyCachedTracks(manifest);
  if (existingTracks.length === totalExpectedTracks) {
    return existingTracks;
  }

  return new Promise((resolve, reject) => {
    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) {
        return;
      }
      cleanedUp = true;
      clearTimeout(timeoutHandle);
      foregroundPortalGate.off('audiobookSingleTrackDownloaded', handler);
    };

    const timeoutHandle = setTimeout(() => {
      cleanup();
      reject(new Error(`Audiobook download timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    const handler = async (event: { docId: string; trackId: string; }) => {
      if (event.docId !== docId) {
        return;
      }

      const tracks = await getLocallyCachedTracks(manifest);
      if (tracks.length === totalExpectedTracks) {
        cleanup();
        logger.info('Audiobook download completed', {
          docId,
          trackCount: tracks.length,
          waitTimeMs: nowTimestamp() - startTime,
        });
        resolve(tracks);
      }
    };

    foregroundPortalGate.on('audiobookSingleTrackDownloaded', handler);
  });
}


export function loadAudiobookTracksInBackground(): void {
  if (hasStartedBackgroundLoop) {
    logger.debug('Audiobook background loader already running, skipping start');
    return;
  }
  hasStartedBackgroundLoop = true;
  logger.info('Starting audiobook background loader');
  loadTrackBatchFromServer().catch((error) => {
    logger.error('Error in background audiobook loader', { error });
  });
}

function hasUndownloadableTracks(
  manifest: AudiobookManifest,
  encryptedFileMap: AudiobookEncryptedFileMap | undefined,
  trackIds: string[],
): boolean {
  const manifestEntries = Object.values(manifest);
  for (const trackId of trackIds) {
    const entry = manifestEntries.find((e) => e.id === trackId);
    if (!entry) {
      continue;
    }
    const canDownload = entry.is_encrypted
      ? Boolean(encryptedFileMap?.[entry.internal_id])
      : Boolean(entry.plaintext_data_file_url);
    if (!canDownload) {
      return true;
    }
  }
  return false;
}

async function refreshEncryptedFileMapIfNeeded(
  docId: string,
  parsedDocId: string,
  manifest: AudiobookManifest,
  encryptedFileMap: AudiobookEncryptedFileMap | undefined,
  trackIds: string[],
): Promise<AudiobookEncryptedFileMap | undefined> {
  if (!hasUndownloadableTracks(manifest, encryptedFileMap, trackIds)) {
    return encryptedFileMap;
  }

  logger.info('Tracks missing download URLs, attempting refresh from server', { docId, parsedDocId });
  if (!parsedDocId) {
    return encryptedFileMap;
  }

  const refreshedContent = await stores.documentContent.loadByIds([parsedDocId], true);
  const entry = refreshedContent?.[parsedDocId];
  const refreshedFileMap = entry && isAudiobookDocumentContent(entry) ? entry.encrypted_file_map : null;
  if (refreshedFileMap && Object.keys(refreshedFileMap).length > 0) {
    logger.info('Refreshed encrypted file map from server', {
      docId,
      entryCount: Object.keys(refreshedFileMap).length,
    });
    return refreshedFileMap;
  }

  return encryptedFileMap;
}

async function downloadTrackBatch(docBatches: DocTrackBatch[]): Promise<void> {
  for (const { docId, parsedDocId, manifest, encryptedFileMap, trackIds, profileId } of docBatches) {
    try {
      const fileMapToUse = await refreshEncryptedFileMapIfNeeded(
        docId, parsedDocId, manifest, encryptedFileMap, trackIds,
      );

      const decryptedFileMap: AudiobookDecryptedFileMap | undefined = profileId
        ? await decryptAudiobookFileMap(fileMapToUse, {
          profileId,
          logContext: { docId, parsedDocId },
        })
        : fileMapToUse as AudiobookDecryptedFileMap | undefined;
      const downloadedTracks = await audiobookFileStore.downloadTracks(docId, manifest, decryptedFileMap ?? null, {
        trackIdsInPriorityOrder: trackIds,
      });
      audiobookProgressTracker.handleItemsLoaded(downloadedTracks);
    } catch (error) {
      if (error instanceof DeviceNotRegisteredError && profileId) {
        logger.info('Device not registered during track download, re-registering and retrying', { docId, profileId });
        try {
          await forceReRegister(profileId);
          const retryFileMap = await refreshEncryptedFileMapIfNeeded(docId, parsedDocId, manifest, encryptedFileMap, trackIds);
          const retryDecryptedFileMap = await decryptAudiobookFileMap(retryFileMap, {
            profileId,
            logContext: { docId, parsedDocId },
          });
          const downloadedTracks = await audiobookFileStore.downloadTracks(docId, manifest, retryDecryptedFileMap ?? null, {
            trackIdsInPriorityOrder: trackIds,
          });
          audiobookProgressTracker.handleItemsLoaded(downloadedTracks);
        } catch (retryError) {
          logger.error('Retry after device re-registration failed', {
            docId,
            error: retryError instanceof Error ? retryError.message : String(retryError),
          });
        }
        continue;
      }
      logger.error('Error downloading track batch for doc', {
        docId,
        trackCount: trackIds.length,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

function scheduleNextLoad(): void {
  setTimeout(() => {
    loadTrackBatchFromServer().catch((error) => {
      logger.error('Error in background audiobook loader', { error });
    });
  }, MIN_TIME_BETWEEN_LOADS_FROM_SERVER);
}

async function processDocForBatch(
  docId: string,
  remainingTrackSlots: number,
): Promise<DocTrackBatch | null> {
  const queueData = await queuedManifestsCache.getItem<QueuedManifestData>(docId);
  if (!queueData) {
    logger.debug('Manifest data missing, skipping doc', { docId });
    return null;
  }

  const parsedDocId = queueData.parsedDocId ?? '';
  const { manifest, encryptedFileMap, profileId } = queueData;
  const allTrackIds = extractTrackIdsFromManifest(manifest);

  if (allTrackIds.length === 0) {
    logger.warn('Manifest contains no tracks, removing from queue', { docId });
    await queuedManifestsCache.removeItem(docId);
    return null;
  }

  const downloadedTracks = await getLocallyCachedTracks(manifest);
  const downloadedTrackIds = downloadedTracks.map((track) => track.id);
  const remainingTrackIds = allTrackIds.filter((id) => !downloadedTrackIds.includes(id));

  if (!queueData.parsedDocId && hasUndownloadableTracks(manifest, encryptedFileMap, remainingTrackIds)) {
    logger.warn('Queued audiobook has undownloadable tracks and no parsedDocId for refresh, removing from queue', { docId });
    await queuedManifestsCache.removeItem(docId);
    return null;
  }

  if (remainingTrackIds.length === 0) {
    logger.info('All tracks downloaded for doc, removing from queue', { docId });
    await queuedManifestsCache.removeItem(docId);
    return null;
  }

  const tracksToAddCount = Math.min(remainingTrackIds.length, remainingTrackSlots);

  return {
    docId,
    parsedDocId,
    manifest,
    encryptedFileMap,
    trackIds: remainingTrackIds.slice(0, tracksToAddCount),
    profileId,
  };
}

async function buildDocBatches(
  sortedDocIds: string[],
  maxTracks: number,
): Promise<DocTrackBatch[]> {
  const docBatches: DocTrackBatch[] = [];
  let totalTrackCount = 0;

  for (const docId of sortedDocIds) {
    if (totalTrackCount >= maxTracks) {
      break;
    }

    const batch = await processDocForBatch(docId, maxTracks - totalTrackCount);
    if (batch) {
      docBatches.push(batch);
      totalTrackCount += batch.trackIds.length;
    }
  }

  return docBatches;
}

async function loadTrackBatchFromServer(): Promise<void> {
  const docIdsInQueue = await queuedManifestsCache.keys();

  if (docIdsInQueue.length === 0) {
    logger.info('No queued audiobook manifests available, waiting for next poll');
    scheduleNextLoad();
    return;
  }

  logger.info('Processing queued audiobook manifests', { queue: docIdsInQueue });

  const docBatches = await buildDocBatches(docIdsInQueue, MAX_TRACKS_PER_BATCH);

  if (docBatches.length > 0) {
    const totalTracks = docBatches.reduce((sum, batch) => sum + batch.trackIds.length, 0);
    logger.info('Loading track batch', {
      trackCount: totalTracks,
      docCount: docBatches.length,
    });
    await downloadTrackBatch(docBatches);
  }

  scheduleNextLoad();
}


function extractTrackIdsFromManifest(manifest: AudiobookManifest): string[] {
  return sortManifestByIndex(manifest)
    .map((entry) => entry.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * Purges audiobook content for documents in a specific location.
 * Extracts audiobook manifests from the provided cached content, removes queued downloads, and removes cached tracks.
 */
export async function purgeAudiobooksForDocuments(
  docs: AnyDocument[],
  locationName: string,
  cachedContentByParsedDocId: { [parsedDocId: string]: DocumentContent | undefined; },
  isFeedLocation = false,
): Promise<{
  removedAudiobookTrackIds: string[];
  removedQueuedDocIds: string[];
}> {
  if (isFeedLocation) {
    return {
      removedAudiobookTrackIds: [],
      removedQueuedDocIds: [],
    };
  }

  const startTime = performance.now();
  const audiobookTrackIds = new Set<string>();
  const audiobookDocIds = new Set<string>();

  // Collect parsed_doc_ids for audiobook documents
  const audiobookDocs = docs.filter((doc): doc is FirstClassDocument =>
    doc.category === Category.Audiobook && 'parsed_doc_id' in doc && Boolean(doc.parsed_doc_id));

  if (audiobookDocs.length > 0) {
    for (const doc of audiobookDocs) {
      const content = cachedContentByParsedDocId[String(doc.parsed_doc_id)];
      if (content && isAudiobookDocumentContent(content) && content.manifest) {
        audiobookDocIds.add(doc.id);
        for (const entry of Object.values(content.manifest)) {
          if (entry.id) {
            audiobookTrackIds.add(entry.id);
          }
        }
      }
    }
  }

  logger.debug('AudiobookPurger: extracted manifest data from cache', {
    location: locationName,
    audiobookDocCount: audiobookDocIds.size,
    trackCount: audiobookTrackIds.size,
    duration: performance.now() - startTime,
  });

  const removeQueuedStart = performance.now();
  if (audiobookDocIds.size > 0) {
    await removeQueuedAudiobookManifestsForDocIds(Array.from(audiobookDocIds));
  }
  const removeQueuedEnd = performance.now();

  const removeTracksStart = performance.now();
  if (audiobookTrackIds.size > 0) {
    await removeTracksByIds(Array.from(audiobookTrackIds));
  }
  const removeTracksEnd = performance.now();

  logger.debug('AudiobookPurger: completed purging', {
    location: locationName,
    removedQueued: audiobookDocIds.size,
    removedTracks: audiobookTrackIds.size,
    timing: {
      removeQueued: removeQueuedEnd - removeQueuedStart,
      removeTracks: removeTracksEnd - removeTracksStart,
      total: performance.now() - startTime,
    },
  });

  return {
    removedAudiobookTrackIds: Array.from(audiobookTrackIds),
    removedQueuedDocIds: Array.from(audiobookDocIds),
  };
}
